import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";
import ChannelSelector from "../components/ChannelSelector";
import SentimentScoreChart from "../components/SentimentScoreChart";
import SentimentDistributionChart from "../components/SentimentDistributionChart";
import TopFrequentWordsCard from "../components/TopFrequentWordsCard";
import ChannelSentimentTable from "../components/ChannelSentimentTable";
import SyncButtonWithTimestamp from '../components/SyncButtonWithTimestamp';
import EmptyState from '../components/EmptyState';

// Import Slack data service functions
import {
  fetchOrganization,
  checkSlackConnection,
  fetchSlackChannels,
  clearCache
} from "../utils/slackDataService";

// Import report service functions
import {
  fetchReport,
  calculateTopFrequentWords,
  calculateSentimentDistributionData
} from "../utils/slackReportService";

import { shouldBypassSlackConnection } from "../utils/config";

const normalizeSentimentPercents = (dist) => {
  const clamp01 = (v) => {
    if (typeof v !== "number" || Number.isNaN(v)) return 0;
    return Math.max(0, Math.min(1, v));
  };

  const pRaw = clamp01(dist?.positive);
  const nRaw = clamp01(dist?.negative);
  const uRaw = typeof dist?.neutral === "number" ? clamp01(dist.neutral) : null;

  // If backend doesn't provide neutral, infer it.
  const uInferred = uRaw === null ? clamp01(1 - pRaw - nRaw) : uRaw;

  const total = pRaw + uInferred + nRaw;
  if (total <= 0) return { positive: 0, neutral: 0, negative: 0 };

  const parts = [
    { key: "positive", value: (pRaw / total) * 100 },
    { key: "neutral", value: (uInferred / total) * 100 },
    { key: "negative", value: (nRaw / total) * 100 },
  ];

  // Largest remainder method so integers sum to 100.
  parts.forEach((p) => {
    p.floor = Math.floor(p.value);
    p.frac = p.value - p.floor;
  });
  let remaining = 100 - parts.reduce((s, p) => s + p.floor, 0);
  parts
    .sort((a, b) => b.frac - a.frac)
    .forEach((p) => {
      if (remaining <= 0) return;
      p.floor += 1;
      remaining -= 1;
    });

  const result = {};
  parts.forEach((p) => {
    result[p.key] = p.floor;
  });
  return result;
};

const SentimentAnalysis = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [slackConnected, setSlackConnected] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Initialize dateRange with "last30days"
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const endDate = new Date(today);
    return { startDate, endDate, range: "last30days" };
  });
  
  const [selectedChannels, setSelectedChannels] = useState([]);
  
  // Data states
  const [sentimentScoreData, setSentimentScoreData] = useState([]);
  const [sentimentDistributionData, setSentimentDistributionData] = useState([]);
  const [channelSentimentData, setChannelSentimentData] = useState([]);
  const [topFrequentWords, setTopFrequentWords] = useState([]);
  
  // Check user session and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Retrieve user data from localStorage
      const userData = localStorage.getItem("user");
      if (userData && userData !== "undefined") {
        try {
          setLoading(true);
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          if (parsedUser.organizations && parsedUser.organizations.length > 0) {
            const firstOrgId = parsedUser.organizations[0].organizationId;
            const org = await fetchOrganization(firstOrgId);
            setOrganization(org);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          setError("Error loading user data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    };
    
    loadInitialData();
  }, [navigate]);
  
  // Check Slack connection and load Slack data
  useEffect(() => {
    const loadSlackData = async () => {
      if (!organization) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // If we should bypass, set slackConnected to true and proceed
        if (shouldBypassSlackConnection()) {
          console.log('SentimentAnalysis.jsx: Bypassing Slack check, setting slackConnected=true');
          setSlackConnected(true);
          const channels = await fetchSlackChannels(organization);
          setAvailableChannels(channels);
        } else {
          // Check if Slack is connected
          const isConnected = await checkSlackConnection(organization);
          setSlackConnected(isConnected);
          
          if (isConnected) {
            // Load channels
            const channels = await fetchSlackChannels(organization);
            setAvailableChannels(channels);
          } else {
            setError("Slack is not connected. Please connect Slack in the Integrations page.");
          }
        }
      } catch (error) {
        console.error("Error loading Slack data:", error);
        if (!shouldBypassSlackConnection()) {
          setError("Error checking Slack connection. Please try again.");
        }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadSlackData();
  }, [organization]);

  // Load sentiment data when slackConnected becomes true
  useEffect(() => {
    if (slackConnected && organization && initialLoadComplete) {
      loadSentimentData(false);
    }
  }, [slackConnected, organization, initialLoadComplete]);
  
  // Load sentiment data based on filters
  const loadSentimentData = async (isFilterChange = true, channelsOverride = null) => {
    if (!organization || !slackConnected) {
      console.log("Cannot load data: organization or slack not connected");
      return;
    }
    
    try {
      if (isFilterChange) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setNoDataAvailable(false);
      
      // Force clear the cache when loading fresh data with filters
      clearCache();
      
      // Use channelsOverride if provided, otherwise use selectedChannels state
      const channelsToUse = channelsOverride !== null ? channelsOverride : selectedChannels;
      
      console.log("Loading sentiment data with filter:", { 
        dateRange, 
        selectedChannels: channelsToUse,
        isFilterChange 
      });
      
      // Ensure we have valid date objects
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      console.log(`Date range: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`);
      
      // Get channel names to filter by (since backend uses names as keys)
      const channelNames = channelsToUse.length > 0 
        ? channelsToUse
        : null;
      
      // First, fetch the report data from the database
      const reportResult = await fetchReport(organization, {
        channels: channelNames
      });
      
      // Extract sentiment data directly from the filtered report
      let timeData = [];
      let distributionData = [];
      let channelData = [];
      let wordsData = [];
      
      if (reportResult && reportResult.success && reportResult.data) {
        const sentimentData = reportResult.data.reportData?.sentiment;
        
        console.log("Sentiment data from report:", sentimentData);
        console.log("Available sentiment keys:", sentimentData ? Object.keys(sentimentData) : "No sentiment data");
        
        if (sentimentData) {
          const startDateStr = startDate.toISOString().split('T')[0];
          const endDateStr = endDate.toISOString().split('T')[0];
          
          // Calculate time series data from sentiment distribution
          if (sentimentData.sentimentDistribution) {
            const MONTH_NUMS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
            Object.entries(sentimentData.sentimentDistribution).forEach(([dateStr, dist]) => {
              const datePart = dateStr.split(',')[0].trim();
              const [mName, mDay, mYear] = datePart.split(' ');
              const monthIdx = MONTH_NUMS[mName];
              const dayNum = parseInt(mDay);
              const yearNum = parseInt(mYear);
              const dateObj = new Date(yearNum, monthIdx, dayNum);
              const isoDate = `${yearNum}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
              
              if (dateObj >= startDate && dateObj <= endDate) {
                const normalized = normalizeSentimentPercents(dist);
                timeData.push({
                  date: isoDate,
                  ...normalized,
                });
              }
            });
            
            timeData.sort((a, b) => new Date(a.date) - new Date(b.date));
          }
          
          // Calculate distribution data from filtered report
          distributionData = calculateSentimentDistributionData(reportResult.data.reportData, startDateStr, endDateStr);
          
          // Calculate frequent words from filtered report
          wordsData = calculateTopFrequentWords(reportResult.data.reportData, startDateStr, endDateStr, 10);
          
          // Extract and aggregate channel sentiment data from filtered report
          const rawChannelData = reportResult.data.reportData.sentiment?.channelSentimentArray || [];
          console.log("Raw channel sentiment data:", rawChannelData);
          
          // Filter raw channel data by date range
          const filteredChannelData = rawChannelData.filter(item => {
            if (!item.date) return true; // Include items without date info for backwards compatibility
            
            const itemDate = new Date(item.date.split(',')[0].trim()); // Parse date from "Jun 1 2025, Monday" format
            return itemDate >= startDate && itemDate <= endDate;
          });
          
          console.log(`Filtered channel sentiment data: ${filteredChannelData.length} entries (from ${rawChannelData.length} total) for date range ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`);
          
          // Group by channel and aggregate scores
          const channelMap = new Map();
          
          filteredChannelData.forEach(item => {
            const channelName = item.channel;
            if (!channelMap.has(channelName)) {
              channelMap.set(channelName, {
                channel: channelName,
                channelId: item.channelId,
                is_private: item.is_private,
                positiveValues: [],
                neutralValues: [],
                negativeValues: [],
                allFrequentWords: []
              });
            }
            
            const channelData = channelMap.get(channelName);
            
            if (item.positive != null) channelData.positiveValues.push(item.positive);
            if (item.neutral != null) channelData.neutralValues.push(item.neutral);
            if (item.negative != null) channelData.negativeValues.push(item.negative);
            
            if (item.frequentWords && Array.isArray(item.frequentWords)) {
              channelData.allFrequentWords.push(...item.frequentWords);
            }
          });
          
          // Calculate aggregated data for each channel
          channelData = Array.from(channelMap.values()).map(channel => {
            const avg = (arr) => arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
            const avgPositive = avg(channel.positiveValues);
            const avgNeutral = avg(channel.neutralValues);
            const avgNegative = avg(channel.negativeValues);
            
            const wordFrequency = new Map();
            const wordSentiment = new Map();
            channel.allFrequentWords.forEach(word => {
              if (word && word.text) {
                wordFrequency.set(word.text, (wordFrequency.get(word.text) || 0) + (word.count || 1));
                if (word.sentiment) wordSentiment.set(word.text, word.sentiment);
              }
            });
            
            const topWords = Array.from(wordFrequency.entries())
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([text, count]) => ({ text, count, sentiment: wordSentiment.get(text) || 'neutral' }));
            
            return {
              id: channel.channelId || channel.channel,
              channel: channel.channel,
              channelId: channel.channelId,
              is_private: channel.is_private || false,
              positive: Math.round(avgPositive * 100),
              neutral: Math.round(avgNeutral * 100),
              negative: Math.round(avgNegative * 100),
              frequentWords: topWords
            };
          }).filter(channel => (channel.positive + channel.neutral + channel.negative) > 0);
          
          // Enrich channel data with is_private property from Slack channels API
          try {
            const slackChannels = await fetchSlackChannels(organization);
            const channelPrivateMap = new Map(slackChannels.map(ch => [ch.name, ch.is_private]));
            channelData = channelData.map(item => ({
              ...item,
              is_private: channelPrivateMap.has(item.channel) ? channelPrivateMap.get(item.channel) : (item.is_private || false)
            }));
          } catch (error) {
            console.warn("Could not fetch channels for is_private property:", error);
          }

          console.log("Aggregated channel sentiment data:", channelData);
        }
      }
      
      console.log(`Loaded data: ${timeData.length} time points, ${distributionData.length} distribution values, ${channelData.length} channels, ${wordsData.length} words`);
      
      setSentimentScoreData(timeData);
      setSentimentDistributionData(distributionData);
      setChannelSentimentData(channelData);
      setTopFrequentWords(wordsData);
      
      // Check if we have any real data
      const hasTimeData = timeData.length > 0;
      const hasDistributionData = distributionData.length > 0;
      const hasChannelData = channelData.length > 0;
      const hasWordsData = wordsData.length > 0;
      
      console.log("Data status:", { hasTimeData, hasDistributionData, hasChannelData, hasWordsData });
      
      if (!hasTimeData && !hasDistributionData && !hasChannelData && !hasWordsData) {
        setNoDataAvailable(true);
      } else {
        setNoDataAvailable(false);
      }
      
    } catch (error) {
      console.error("Error loading sentiment data:", error);
      setError("Error loading sentiment data. Some data may be incomplete.");
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };
  
  // Handle date range change - stabilized with useCallback
  const handleDateRangeChange = useCallback((newRange) => {
    console.log("Date range changed:", newRange);
    
    // Validate the date range before proceeding
    if (!newRange || !newRange.startDate || !newRange.endDate) {
      console.warn("Invalid date range received:", newRange);
      return;
    }
    
    // Update UI state first
    setSentimentScoreData([]);
    setSentimentDistributionData([]);
    setChannelSentimentData([]);
    setTopFrequentWords([]);
    setDataLoading(true);
    
    // Update the date range state
    setDateRange(newRange);
  }, []);

  // Load data when dateRange changes (for filter changes)
  useEffect(() => {
    if (dateRange && organization && slackConnected && initialLoadComplete) {
      // Use setTimeout to ensure state is updated before loading data
      setTimeout(() => {
        loadSentimentData(true);
      }, 0);
    }
  }, [dateRange, organization, slackConnected, initialLoadComplete]);
  
  // Handle channel selection change
  const handleChannelChange = (channels) => {
    setSelectedChannels(channels);
    loadSentimentData(true, channels); // Pass the new channels directly
  };
  
  // Filter channel data based on selection
  const filteredChannelData = selectedChannels.length > 0
    ? channelSentimentData.filter(channel => selectedChannels.includes(channel.channel))
    : channelSentimentData;
  
  // Show loading indicator for the entire page during initial load
  if (loading && !initialLoadComplete) {
    return (
      <div className="flex h-screen h-dvh overflow-hidden bg-grey-10">
        <Sidebar user={user} organization={organization} organizations={organizations} />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-grey-40 border-t-brand rounded-full animate-spin inline-block mb-4"></div>
            <p className="text-grey-60">Loading sentiment data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex h-screen h-dvh overflow-hidden bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} organizations={organizations} />
      
      <div className="flex-1  overflow-y-auto">
        {/* Header and filters */}
        <div className="p-5 pb-0">
          <h1 className="text-xl font-normal text-black-0">Sentiment analysis</h1>
        </div>
        
        {/* Slack not connected message */}
        {!slackConnected && !loading && (
          <div className="bg-grey-20 p-4 rounded-md my-4">
            <p className="text-grey-60">
              You need to connect to Slack first. Please go to the{" "}
              <button 
                onClick={() => navigate('/settings')}
                className="text-brand hover:underline"
              >
                Settings page
              </button>{" "}
              to set up your Slack connection.
            </p>
          </div>
        )}
        
        {/* Error message */}
          {error && (
          <div className="bg-red-light p-4 rounded-md my-4">
            <p className="text-red-strong">{error}</p>
          </div>
        )}
        
        {/* No data available message - only show in production */}
        {noDataAvailable && slackConnected && !loading && !dataLoading && !shouldBypassSlackConnection() && (
          <EmptyState
            variant="no-data"
            title="No sentiment data available"
            description="The Slack bot may not be a member of any channels, or there are no messages in the selected channels. Try adding the bot to more channels in Slack or selecting different channels."
            actionButton={null}
          />
        )}
          
        {/* Main content - only show if Slack is connected and data is loaded */}
          {slackConnected && (
          <>
            {/* Filters */}
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <ChannelSelector 
                  availableChannels={availableChannels}
                  selectedChannels={selectedChannels}
                  onChange={handleChannelChange}
                />
              </div>
              <SyncButtonWithTimestamp organization={organization} dateRange={dateRange} onSyncComplete={() => window.location.reload()} />
            </div>
            
            {/* Loading indicator for filter changes */}
            {dataLoading && (
              <div className="flex justify-center items-center py-4 mb-4">
                <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin mr-3"></div>
                <span className="text-grey-60">Updating data...</span>
            </div>
            )}
            
            {/* Charts row */}
            <div className="flex gap-4 p-5 pt-0">
              <div className="flex-1">
                <SentimentScoreChart data={sentimentScoreData} />
              </div>
              <div style={{ width: '300px' }}>
                <SentimentDistributionChart data={sentimentDistributionData} />
              </div>
              <div style={{ width: '300px' }}>
                <TopFrequentWordsCard words={topFrequentWords} />
              </div>
            </div>
            
            {/* Channel sentiment table */}
                <ChannelSentimentTable data={filteredChannelData} />
            
            {/* Empty data message when tables/charts are empty */}
            {filteredChannelData.length === 0 && !dataLoading && !noDataAvailable && (
              <div className="mt-4">
                <EmptyState
                  variant="search"
                  title="No channel data matches your filters"
                  description="Try adjusting your date range or channel selection to see sentiment data."
                  actionButton={null}
                />
              </div>
            )}
          </>
          )}
      </div>
    </div>
  );
};

export default SentimentAnalysis; 